import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Tag } from 'lucide-react';
import { TagChip, TAG_COLORS, type TagColor } from '../../atoms/TagChip';
import './TagPanel.css';

export interface TagDefinition {
  id: string;
  name: string;
  color: TagColor;
}

export interface TagPanelProps {
  /** All workspace-level tag definitions */
  tags: TagDefinition[];
  /** Tag IDs enabled for this document */
  enabledTagIds: string[];
  /** Toggle a tag on/off for this document */
  onToggleTag: (tagId: string) => void;
  /** Create a new tag */
  onCreateTag: (name: string, color: TagColor) => void;
  /** Update an existing tag */
  onEditTag: (tagId: string, name: string, color: TagColor) => void;
  /** Delete a tag from workspace */
  onDeleteTag: (tagId: string) => void;
  /** Label for the trigger button tooltip */
  triggerLabel?: string;
  /** Labels for i18n */
  labels?: {
    placeholder?: string;
    done?: string;
    deleteTag?: string;
    cancel?: string;
  };
}

type PanelView = 'list' | 'edit';

const COLOR_SWATCHES: TagColor[] = [...TAG_COLORS];

export const TagPanel: React.FC<TagPanelProps> = ({
  tags,
  enabledTagIds,
  onToggleTag,
  onCreateTag,
  onEditTag,
  onDeleteTag,
  triggerLabel = 'Tags',
  labels = {},
}) => {
  const {
    placeholder = 'Enter tag...',
    done = 'Done',
    deleteTag = 'Delete tag',
    cancel = 'Cancel',
  } = labels;

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<PanelView>('list');
  const [editingTag, setEditingTag] = useState<TagDefinition | null>(null);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState<TagColor>('darkBlue');
  const [showColorPicker, setShowColorPicker] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close panel on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showColorPicker) {
          setShowColorPicker(false);
        } else if (view === 'edit') {
          resetEditState();
        } else {
          handleClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, view, showColorPicker]);

  // Focus input when entering list view
  useEffect(() => {
    if (isOpen && view === 'list') {
      // Small delay to allow panel to render
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, view]);

  const handleClose = () => {
    setIsOpen(false);
    setView('list');
    setEditingTag(null);
    setNewTagName('');
    setSelectedColor('darkBlue');
    setShowColorPicker(false);
  };

  const resetEditState = () => {
    setView('list');
    setEditingTag(null);
    setNewTagName('');
    setShowColorPicker(false);
  };

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  }, []);

  const handleTagToggle = useCallback((e: React.MouseEvent, tagId: string) => {
    e.stopPropagation();
    onToggleTag(tagId);
  }, [onToggleTag]);

  const handleTagEdit = useCallback((e: React.MouseEvent, tag: TagDefinition) => {
    e.stopPropagation();
    setEditingTag(tag);
    setNewTagName(tag.name);
    setSelectedColor(tag.color);
    setView('edit');
    setShowColorPicker(false);
  }, []);

  const handleCreateTag = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newTagName.trim()) {
      e.stopPropagation();
      onCreateTag(newTagName.trim(), selectedColor);
      setNewTagName('');
    }
  }, [newTagName, selectedColor, onCreateTag]);

  const handleEditDone = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (editingTag && newTagName.trim()) {
      onEditTag(editingTag.id, newTagName.trim(), selectedColor);
    }
    resetEditState();
  }, [editingTag, newTagName, selectedColor, onEditTag]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (editingTag) {
      onDeleteTag(editingTag.id);
    }
    resetEditState();
  }, [editingTag, onDeleteTag]);

  const handleColorSelect = useCallback((e: React.MouseEvent, color: TagColor) => {
    e.stopPropagation();
    setSelectedColor(color);
    setShowColorPicker(false);
  }, []);

  const handleColorSwatchClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowColorPicker((prev) => !prev);
  }, []);

  const handleCancel = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    resetEditState();
  }, []);

  const hasEnabledTags = enabledTagIds.length > 0;

  return (
    <div className="tag-panel-wrapper">
      <button
        ref={triggerRef}
        className={`tag-panel-trigger${hasEnabledTags ? ' tag-panel-trigger-active' : ''}`}
        onClick={handleToggle}
        aria-label={triggerLabel}
        title={triggerLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <Tag className="tag-panel-trigger-icon" />
      </button>

      {isOpen && (
        <div ref={panelRef} className="tag-panel" role="dialog" aria-label={triggerLabel}>
          {view === 'list' && (
            <div className="tag-panel-list">
              {/* New tag input */}
              <div className="tag-panel-input-row">
                <button
                  className="tag-panel-color-swatch"
                  onClick={handleColorSwatchClick}
                  aria-label="Pick color"
                >
                  <span className={`tag-panel-color-dot tag-panel-color-dot-${selectedColor}`} />
                </button>
                <input
                  ref={inputRef}
                  className="tag-panel-input"
                  type="text"
                  placeholder={placeholder}
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={handleCreateTag}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Color picker inline (shown below input when swatch clicked) */}
              {showColorPicker && (
                <div className="tag-panel-color-picker">
                  {COLOR_SWATCHES.map((color) => (
                    <button
                      key={color}
                      className={`tag-panel-color-option${color === selectedColor ? ' tag-panel-color-option-selected' : ''}`}
                      onClick={(e) => handleColorSelect(e, color)}
                      aria-label={color}
                    >
                      <span className={`tag-panel-color-dot tag-panel-color-dot-${color}`} />
                    </button>
                  ))}
                </div>
              )}

              <div className="tag-panel-divider" />

              {/* Tag cloud */}
              <div className="tag-panel-tags">
                {tags.map((tag) => {
                  const isEnabled = enabledTagIds.includes(tag.id);
                  return (
                    <div
                      key={tag.id}
                      className={`tag-panel-tag-item${isEnabled ? ' tag-panel-tag-item-enabled' : ''}`}
                    >
                      <button
                        className="tag-panel-tag-toggle"
                        onClick={(e) => handleTagToggle(e, tag.id)}
                        aria-label={`${isEnabled ? 'Disable' : 'Enable'} tag ${tag.name}`}
                      >
                        <TagChip
                          name={tag.name}
                          color={tag.color}
                          size="md"
                        />
                      </button>
                      <button
                        className="tag-panel-tag-edit"
                        onClick={(e) => handleTagEdit(e, tag)}
                        aria-label={`Edit tag ${tag.name}`}
                      >
                        <EditIcon />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === 'edit' && editingTag && (
            <div className="tag-panel-editor">
              {/* Color swatch + name input */}
              <div className="tag-panel-input-row">
                <button
                  className="tag-panel-color-swatch"
                  onClick={handleColorSwatchClick}
                  aria-label="Pick color"
                >
                  <span className={`tag-panel-color-dot tag-panel-color-dot-${selectedColor}`} />
                </button>
                <input
                  className="tag-panel-input"
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <button
                  className="tag-panel-done-button"
                  onClick={handleEditDone}
                >
                  {done}
                </button>
              </div>

              {/* Color picker */}
              {showColorPicker && (
                <div className="tag-panel-color-picker">
                  {COLOR_SWATCHES.map((color) => (
                    <button
                      key={color}
                      className={`tag-panel-color-option${color === selectedColor ? ' tag-panel-color-option-selected' : ''}`}
                      onClick={(e) => handleColorSelect(e, color)}
                      aria-label={color}
                    >
                      <span className={`tag-panel-color-dot tag-panel-color-dot-${color}`} />
                    </button>
                  ))}
                </div>
              )}

              <div className="tag-panel-divider" />

              {/* Actions */}
              <div className="tag-panel-editor-actions">
                <button
                  className="tag-panel-action-delete"
                  onClick={handleDelete}
                >
                  {deleteTag}
                </button>
                <button
                  className="tag-panel-action-cancel"
                  onClick={handleCancel}
                >
                  {cancel}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Small pencil/edit icon inline SVG */
const EditIcon: React.FC = () => (
  <svg
    className="tag-panel-edit-icon"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.5 1.5L14.5 4.5L5 14H2V11L11.5 1.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
